import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePathParam, useQueryParam, useResource, useRouterActions } from 'react-resource-router';
import uuid from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import {
	getDetailUrl,
	getListUrl,
	QUERY_PARAMETER_QUERY_LABEL,
	QUERY_PARAMETER_VIEW_LABEL,
} from '@atlassian/eoc-alert-common';
import { isServer } from '@atlassian/eoc-common';
import {
	BOOLEAN_LITERAL_FALSE,
	IDENTIFIER_ACKNOWLEDGED,
	IDENTIFIER_ASSIGNEE,
	IDENTIFIER_IS_SEEN,
	IDENTIFIER_STATUS,
	LOGICAL_OPERATOR_AND,
	OPERATOR_EQUAL_TO,
	STRING_LITERAL_STATUS_CLOSED,
	STRING_LITERAL_STATUS_OPEN,
} from '@atlassian/eoc-query-editor';
import { eocUserResource } from '@atlassian/eoc-resources';

import { type SavedSearch } from '../../common/types';
import { getDefaultFilterQuery, withQuote } from '../../common/utils';
import { useSavedSearchService } from '../../services/saved-searches';
import { savedSearchesResource } from '../../services/saved-searches/resources';
import { useUiPreferencesService } from '../../services/ui-preferences';
import { useSavedSearchRelayUtils } from '../saved-search-relay-utils';

const DEFAULT_LIMIT = 3;

export const useSavedSearchController = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchedQuery] = useQueryParam(QUERY_PARAMETER_QUERY_LABEL);
	const [view] = useQueryParam(QUERY_PARAMETER_VIEW_LABEL);
	const [teamId] = usePathParam('teamId');
	const [alertId] = usePathParam('id');
	const { data: user } = useResource(eocUserResource);
	const { push } = useRouterActions();
	const { removeSavedSearchFromCreatedByMe } = useSavedSearchRelayUtils();

	const defaultFilters = useMemo(() => {
		return {
			allSearch: {
				id: uuid(),
				name: 'All',
				query: getDefaultFilterQuery('', teamId),
			},
			openSearch: {
				id: uuid(),
				name: 'Open',
				query: getDefaultFilterQuery(
					`${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN}`,
					teamId,
				),
			},
			closedSearch: {
				id: uuid(),
				name: 'Closed',
				query: getDefaultFilterQuery(
					`${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_CLOSED}`,
					teamId,
				),
			},
			unackedSearch: {
				id: uuid(),
				name: 'Unacknowledged',
				query: getDefaultFilterQuery(
					`${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN} ${LOGICAL_OPERATOR_AND} ${IDENTIFIER_ACKNOWLEDGED}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
					teamId,
				),
			},
			notSeenSearch: {
				id: uuid(),
				name: 'Not seen',
				query: getDefaultFilterQuery(
					`${IDENTIFIER_IS_SEEN}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
					teamId,
				),
			},
			assignedToMeSearch: {
				id: uuid(),
				name: 'Assigned to me',
				query: getDefaultFilterQuery(
					`${IDENTIFIER_ASSIGNEE}${OPERATOR_EQUAL_TO} ${withQuote(user?.accountId || 'me')}`,
					teamId,
				),
			},
		};
	}, [teamId, user]);

	const { data: savedSearches, loading: isSavedSearchesLoading } =
		useResource(savedSearchesResource);
	const {
		data: uiPreferences,
		isLoading: isUiPreferencesLoading,
		update,
	} = useUiPreferencesService();

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [idToDelete, setIdToDelete] = useState('');

	const openDeleteModal = (id: string) => {
		setIsDeleteModalOpen(true);
		setIdToDelete(id);
	};
	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setIdToDelete('');
	};

	const { isDeleteLoading, deleteSavedSearch } = useSavedSearchService();

	const handleDeleteModalSubmit = () => {
		deleteSavedSearch(idToDelete)
			.then(() => {
				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'savedSearch deleted');
				removeSavedSearchFromCreatedByMe(idToDelete);
			})
			.catch(() => {})
			.finally(() => {
				closeDeleteModal();
			});
	};

	const isPageLoading = isSavedSearchesLoading || isUiPreferencesLoading || isServer();

	const [isOpenSharedWithMe, setIsOpenSharedWithMe] = useState(true);
	const [isOpenCreatedByMe, setIsOpenCreatedByMe] = useState(true);

	const [isSharedWithMeExpanded, setIsSharedWithMeExpanded] = useState(false);
	const [isCreatedByMeExpanded, setIsCreatedByMeExpanded] = useState(false);

	const [selectedItemId, setSelectedItemId] = useState<string>();

	useEffect(() => {
		if (!searchedQuery) {
			setSelectedItemId(defaultFilters.allSearch.id);
			return;
		}

		const allSavedSearches = [
			...Object.values(defaultFilters),
			...(savedSearches?.createdByMe ?? []),
			...(savedSearches?.sharedWithMe ?? []),
		];
		const foundSavedSearch = allSavedSearches.find(
			(savedSearch) => savedSearch.query === searchedQuery,
		);
		setSelectedItemId(foundSavedSearch?.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const createdByMeSearches = useMemo(() => {
		return savedSearches?.createdByMe.slice(
			0,
			isCreatedByMeExpanded ? savedSearches.createdByMe.length : DEFAULT_LIMIT,
		);
	}, [isCreatedByMeExpanded, savedSearches?.createdByMe]);

	const notVisibleCreatedByMeCount = useMemo(() => {
		if (!savedSearches || !createdByMeSearches) {
			return;
		}
		return savedSearches.createdByMe.length - createdByMeSearches.length;
	}, [createdByMeSearches, savedSearches]);

	const sharedWithMeSearches = useMemo(() => {
		return savedSearches?.sharedWithMe.slice(
			0,
			isSharedWithMeExpanded ? savedSearches.sharedWithMe.length : DEFAULT_LIMIT,
		);
	}, [isSharedWithMeExpanded, savedSearches?.sharedWithMe]);

	const notVisibleSharedWithMeCount = useMemo(() => {
		if (!savedSearches || !sharedWithMeSearches) {
			return;
		}
		return savedSearches.sharedWithMe.length - sharedWithMeSearches.length;
	}, [savedSearches, sharedWithMeSearches]);

	const starredItem = useMemo(() => {
		if (!uiPreferences || !savedSearches) {
			return;
		}
		const { createdByMe = [], sharedWithMe = [] } = savedSearches;

		return [...createdByMe, ...sharedWithMe].find(
			(savedSearch) => savedSearch.id === uiPreferences.defaultSavedSearchId,
		);
	}, [savedSearches, uiPreferences]);

	const handleItemClick = useCallback(
		(item: SavedSearch, type: string) => {
			const url =
				view === 'detail'
					? getDetailUrl(alertId, teamId, item.query)
					: getListUrl(teamId, item.query);
			push(url);
			setSelectedItemId(item.id);
			const event = createAnalyticsEvent({});
			fireUIAnalytics(event, 'savedSearch clicked');
		},
		[alertId, createAnalyticsEvent, push, teamId, view],
	);

	const expandSharedWithMe = () => {
		setIsSharedWithMeExpanded(true);
	};

	const expandCreatedByMe = () => {
		setIsCreatedByMeExpanded(true);
	};

	useEffect(() => {
		if (notVisibleCreatedByMeCount === 0) {
			setIsCreatedByMeExpanded(true);
		}
		if (notVisibleSharedWithMeCount === 0) {
			setIsSharedWithMeExpanded(true);
		}
	}, [notVisibleCreatedByMeCount, notVisibleSharedWithMeCount]);

	const handleStar = (id: string) => {
		update({
			defaultSavedSearchId: id,
		});
	};

	return {
		isOpenCreatedByMe,
		isOpenSharedWithMe,
		setIsOpenCreatedByMe,
		setIsOpenSharedWithMe,
		selectedItemId,
		handleItemClick,
		isPageLoading,
		starredItem,
		expandSharedWithMe,
		expandCreatedByMe,
		isSharedWithMeExpanded,
		isCreatedByMeExpanded,
		createdByMeSearches,
		sharedWithMeSearches,
		notVisibleCreatedByMeCount,
		notVisibleSharedWithMeCount,
		defaultFilters,
		handleStar,
		openDeleteModal,
		closeDeleteModal,
		isDeleteLoading,
		handleDeleteModalSubmit,
		isDeleteModalOpen,
	};
};
